import { useEffect, useMemo } from 'react';
import { useLocalStorage } from 'utils/hooks/useLocalStorage';
import { RECENTLY_VIEWED_LOCAL_STORAGE_KEY } from '../../../constants';

export const RECENTLY_VISITED_IDS_MAX_LIMIT = 5;

const updateRecentlyVisitedViews = (currentIds: string[], newViewId?: string) => {
  if (!newViewId) return currentIds;
  // Remove the viewId if it exists to avoid duplicates. And, add the viewId at the start of the array
  const updatedIds = [newViewId, ...currentIds.filter((id) => id !== newViewId)];
  // Ensure the limit is respected
  return updatedIds.slice(0, RECENTLY_VISITED_IDS_MAX_LIMIT);
};

export const useRecentlyVisitedViews = (viewId?: string) => {
  const [recentlyViewedIds, setRecentlyViewedIds] = useLocalStorage<string[]>(
    RECENTLY_VIEWED_LOCAL_STORAGE_KEY,
    [],
  );

  const updatedList = useMemo(() => {
    return updateRecentlyVisitedViews(recentlyViewedIds, viewId);
  }, [recentlyViewedIds, viewId]);

  useEffect(() => {
    /**
     * Update local storage if the new viewId is different from the current first item.
     * This ensures that local storage is only written to when there is an actual change,
     * avoiding unnecessary writes and potential performance issues.
     */
    if (viewId && viewId !== recentlyViewedIds?.[0]) {
      setRecentlyViewedIds(updatedList);
    }
  }, [viewId, updatedList, recentlyViewedIds, setRecentlyViewedIds]);

  return updatedList;
};
